import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { ReCaptcha } from "react-recaptcha-google";
import "./style.scss";
import PrimaryButton from "../../elements/primary-button";
import Input from "../../elements/input";
import Dropdown from "../../elements/dropdown";
import PropTypes from "prop-types";
import Heading from "../../elements/heading";
import Checkbox from "../../elements/checkbox";
import InputPhoneNumber from "../../elements/InputPhoneNumber";
import BodyText from "../../elements/body-text";
import { getMarketList } from "../../services/siteService";
import { S_LANG, webStorage } from "../../utils/webStorage";
import { selectUserState } from "../../selectors/application";

const PdfLeadsForm = ({
  user,
  headerType,
  headerBody,
  firstNameLabel,
  firstNameInfoText,
  firstNameMissingMessage,
  lastNameLabel,
  lastNameInfoText,
  lastNameMissingMessage,
  emailLabel,
  emailInfoText,
  emailWarningMessage,
  emailMissingMessage,
  phoneAreaLabel,
  phoneAreaInfoText,
  phoneLabel,
  phoneInfoText,
  phoneWarningMessage,
  phoneMissingMessage,
  countryLabel,
  countryWarningMessage,
  newsletterCheckboxLabel,
  newsletterCheckboxBody,
  termsAndConditionsCheckboxLabel,
  termsAndConditionsCheckboxBody,
  ctaName,
  colorScheme,
  iconName,
  disclaimerText,
  hasError,
  errorMessage,
  onSubmitForm = () => {},
  showCaptcha,
  captchaSiteKey
}) => {
  const submitRef = useRef(null);
  const [captchaValidated, setCaptchaValidated] = useState(!showCaptcha.value);
  const [captchaIsValid, setCapthchaIsValid] = useState();
  const [emailEntered, setEmailEntered] = useState(user?.email);
  const [emailEnteredIsValid, setEmailEnteredIsValid] = useState(true);
  const [firstnameEntered, setFirstnameEntered] = useState(user?.firstName);
  const [firstnameShowError, setFirstnameShowError] = useState();
  const [lastnameEntered, setLastnameEntered] = useState(user?.lastName);
  const [lastnameShowError, setLastnameShowError] = useState();
  const [phoneNumberEntered, setPhoneNumberEntered] = useState(user?.mobileNo);
  const [phoneAreaEntered, setPhoneAreaEntered] = useState(user?.mobileNoCountryCode);
  const [phoneIsValid, setPhoneIsValid] = useState(true);
  const [submitTimestamp, setSubmitTimestamp] = useState();
  const [emailEnteredShowError, setEmailEnteredShowError] = useState(false);
  const [countrySelected, setCountrySelected] = useState();
  const [countryShowError, setCountryShowError] = useState();
  const [newsletterAgreed, setNewsletterAgreed] = useState();
  const [termsAgreed, setTermsAgreed] = useState(!termsAndConditionsCheckboxLabel || !termsAndConditionsCheckboxLabel.value ? true : false);
  const [showCheckboxError, setShowCheckboxError] = useState();

  const [countryList, setCountryList] = useState([]);
  const [currentCountry, setCurrentCountry] = useState(null);

  const lang = webStorage.get(S_LANG)?.toLowerCase();

  const getMarkets = async () => {
    const marketList = await getMarketList();
    const current = marketList.find(it => new RegExp(`^${it.language?.toLowerCase()}$`, "gi").test(lang));
    let list = [];
    let countries = [];
    for (var country in marketList) {
      var countryName = marketList[country].value.split(" ")[0];
      if (!list.includes(countryName)) {
        list.push(countryName);
        countries.push({
          value: countryName,
          code: marketList[country].language.split("-")[1],
          data: marketList[country]
        });
      }
    }
    setCountryList(countries);

    let dropdownCountry = null;

    if (current) {
      dropdownCountry = {
        value: current.value.split(" ")[0],
        code: current.language.split("-")[1],
        data: current
      };
    }
    setCurrentCountry(dropdownCountry);
    setCountrySelected(dropdownCountry);
  };

  useEffect(() => {
    getMarkets();
  }, [lang]);

  let captcha;
  const captchaContainer = useRef();

  const onLoadRecaptcha = () => captcha && captcha.reset();

  const verifyCallback = (recaptchaToken, grecaptcha) => {
    // Here you will get the final recaptchaToken!!!
    getCaptchFrame().style = "";

    if (recaptchaToken) {
      setCaptchaValidated(true);
    } else {
      grecaptcha.reset();
    }
  };

  const onEmailChange = (evt, isValid) => {
    setEmailEntered(evt.target.value);
    setEmailEnteredIsValid(isValid);
  };

  const onFirstnameChange = evt => {
    setFirstnameEntered(evt.target.value);
  };

  const onLastnameChange = evt => {
    setLastnameEntered(evt.target.value);
  };

  const onPhoneChange = (area, number, isValid) => {
    console.log("onPhoneChange", typeof area, typeof number, isValid);
    setPhoneAreaEntered(area);
    setPhoneNumberEntered(number);
    setPhoneIsValid(isValid);
  };

  const onCountryDropdownChange = value => {
    setCountrySelected(value);
  };

  const onNewsletterCheckmarkChange = value => {
    setNewsletterAgreed(value);
    setShowCheckboxError(false);
  };

  const onTermsCheckmarkChange = value => {
    setTermsAgreed(value);
    setShowCheckboxError(false);
  };

  const submitFormIsValid = () => {
    return emailEnteredIsValid && firstnameEntered && lastnameEntered && phoneIsValid && countrySelected && termsAgreed && ((captchaIsValid && showCaptcha.value) || !showCaptcha.value);
  };

  const onSubmitFormData = () => {
    setSubmitTimestamp(new Date());
    if (submitFormIsValid()) {
      let data = {
        firstname: firstnameEntered,
        lastname: lastnameEntered,
        phoneNumber: phoneNumberEntered,
        phoneArea: phoneAreaEntered,
        email: emailEntered,
        newsletterAgreed: newsletterAgreed,
        termsAgreed: termsAgreed,
        country: countrySelected
      };
      onSubmitForm(data);
    } else {
      if (!firstnameEntered) {
        setFirstnameShowError(true);
      }

      if (!lastnameEntered) {
        setLastnameShowError(true);
      }

      if (!emailEntered) {
        setEmailEnteredShowError(true);
      }

      if (!countrySelected) {
        setCountryShowError(true);
      } else if (countrySelected.code === "default") {
        setCountryShowError(true);
      }

      if (!termsAgreed) {
        setShowCheckboxError(true);
      }
    }
  };

  const getCaptchFrame = () => {
    return captchaContainer.current.querySelector(".captcha iframe");
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (captchaValidated) setCapthchaIsValid(true);
    else {
      getCaptchFrame().style = "padding: 2px 1px 1px 2px; background: red;";
    }
  };

  return (
    <form
      className={"pdf-leads-form"}
      onSubmit={e => {
        handleSubmit(e);
      }}
    >
      {headerBody && <Heading html={headerBody} type={headerType} className="title" typographyClass="type-h1" />}
      {firstNameLabel && (
        <Input
          {...{
            name: "first-name",
            automcomplete: "first-name",
            label: firstNameLabel.value,
            maxLength: 25,
            value: firstnameEntered,
            showError: firstnameShowError,
            infoMessage: firstNameInfoText.value,
            warningMessage: firstNameMissingMessage.value,
            onChange: evt => onFirstnameChange(evt),
            onFocus: () => {
              setFirstnameShowError(false);
            }
          }}
        />
      )}
      <Input
        {...{
          name: "family-name",
          automcomplete: "family-name",
          label: lastNameLabel.value,
          maxLength: 25,
          value: lastnameEntered,
          infoMessage: lastNameInfoText.value,
          warningMessage: lastNameMissingMessage.value,
          showError: lastnameShowError,
          onChange: evt => onLastnameChange(evt),
          onFocus: () => {
            setLastnameShowError(false);
          }
        }}
      />
      <Input
        {...{
          autocomplete: "email",
          name: "email",
          type: "email",
          label: emailLabel.value,
          showError: emailEnteredShowError,
          infoMessage: emailInfoText.value,
          value: emailEntered,
          warningMessage: !emailEnteredIsValid ? emailWarningMessage.value : emailMissingMessage.value,
          onChange: (evt, isValid) => onEmailChange(evt, isValid),
          onFocus: () => {
            setEmailEnteredShowError(false);
          }
        }}
      />
      {phoneAreaLabel && phoneLabel && (
        <InputPhoneNumber
          phoneArea={{
            label: phoneAreaLabel.value,
            value: phoneAreaEntered
          }}
          phoneNumber={{
            label: phoneLabel.value,
            value: phoneNumberEntered
          }}
          infoMessage={phoneAreaInfoText?.value}
          warningMessage={phoneWarningMessage.value}
          missingMessage={phoneMissingMessage.value}
          onChange={(area, number, isValid) => {
            onPhoneChange(area, number, isValid);
          }}
          onInit={(area, number, isValid) => {
            onPhoneChange(area, number, isValid);
          }}
          submitTimestamp={submitTimestamp}
        />
      )}

      {countryList && (
        <Dropdown
          className="country"
          {...{
            placeholder: countryLabel.value,
            warningMessage: countryWarningMessage.value,
            enableFilter: true,
            showError: countryShowError,
            options: countryList,
            selectedOption: currentCountry,
            onChange: value => onCountryDropdownChange(value),
            onFocus: () => {
              setCountryShowError(false);
            }
          }}
        />
      )}

      {/* {newsletterCheckboxLabel &&
      newsletterCheckboxLabel.value &&(
        <Checkbox
          {...{
            label: newsletterCheckboxLabel.value,
            infoText: newsletterCheckboxBody.value,
            checked: newsletterAgreed,
            onChange: value => {
              onNewsletterCheckmarkChange(value);
            }
          }}
        />
      )} */}

      {termsAndConditionsCheckboxLabel && termsAndConditionsCheckboxLabel.value && (
        <Checkbox
          {...{
            label: termsAndConditionsCheckboxLabel.value,
            infoText: termsAndConditionsCheckboxBody.value,
            error: showCheckboxError,
            onChange: value => {
              onTermsCheckmarkChange(value);
            }
          }}
        />
      )}

      {showCaptcha?.value && (
        <div className="captcha" ref={captchaContainer}>
          <ReCaptcha
            ref={el => {
              captcha = el;
            }}
            size="normal"
            data-theme="dark"
            theme="dark"
            render="explicit"
            sitekey={captchaSiteKey}
            onloadCallback={onLoadRecaptcha}
            verifyCallback={captchaToken => {
              verifyCallback(captchaToken, captcha);
            }}
          />
        </div>
      )}

      {errorMessage && hasError && (
        <div
          className="error-message"
          dangerouslySetInnerHTML={{
            __html: errorMessage
          }}
        />
      )}

      {ctaName && (
        <PrimaryButton
          {...{
            colorScheme: colorScheme,
            iconName: iconName,
            link: {
              value: {
                text: ctaName.value
              }
            },
            callback: evt => onSubmitFormData(evt)
          }}
        />
      )}

      {disclaimerText && <BodyText className="disclaimer" html={disclaimerText ? disclaimerText.value : ""} />}
    </form>
  );
};

PdfLeadsForm.propTypes = {
  headerType: PropTypes.object,
  headerBody: PropTypes.object,
  firstNameLabel: PropTypes.object,
  firstNameInfoText: PropTypes.object,
  firstNameMissingMessage: PropTypes.object,
  lastNameLabel: PropTypes.object,
  lastNameInfoText: PropTypes.object,
  lastNameMissingMessage: PropTypes.object,
  emailLabel: PropTypes.object,
  emailInfoText: PropTypes.object,
  emailWarningMessage: PropTypes.object,
  emailMissingMessage: PropTypes.object,
  phoneAreaLabel: PropTypes.object,
  phoneAreaInfoText: PropTypes.object,
  phoneLabel: PropTypes.object,
  phoneInfoText: PropTypes.object,
  phoneWarningMessage: PropTypes.object,
  phoneMissingMessage: PropTypes.object,
  countryLabel: PropTypes.object,
  countryWarningMessage: PropTypes.object,
  newsletterCheckboxLabel: PropTypes.object,
  newsletterCheckboxBody: PropTypes.object,
  termsAndConditionsCheckboxLabel: PropTypes.object,
  termsAndConditionsCheckboxBody: PropTypes.object,
  ctaName: PropTypes.object,
  colorScheme: PropTypes.object,
  iconName: PropTypes.object,
  disclaimerText: PropTypes.object,
  country: PropTypes.object,
  onSubmitForm: PropTypes.func,
  showCaptcha: PropTypes.object,
  captchaSiteKey: PropTypes.string,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string
};

PdfLeadsForm.defaultProps = {
  onSubmitForm: () => {}
};

const mapStateToProps = state => ({
  user: selectUserState(state)
});

export default connect(mapStateToProps)(PdfLeadsForm);
